import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileStack, ShieldCheck, Users, Package, ArrowRight, Sparkles, FileText, Plus, AlertTriangle, CalendarClock,
} from "lucide-react";

const STAT = [
  { key: "bids", label: "Active Bids", icon: FileStack, color: "text-slate-900", bg: "bg-slate-100" },
  { key: "documents", label: "Documents Generated", icon: FileText, color: "text-emerald-700", bg: "bg-emerald-50" },
  { key: "atc_docs", label: "AI ATC Declarations", icon: Sparkles, color: "text-amber-600", bg: "bg-amber-50" },
  { key: "buyers", label: "Saved Buyers", icon: Users, color: "text-slate-900", bg: "bg-slate-100" },
];

export default function Overview() {
  const [stats, setStats] = useState({});
  const [bids, setBids] = useState([]);
  const [soon, setSoon] = useState([]);
  const [popup, setPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bids").then((r) => setBids(r.data)).catch(() => {});
    api.get("/tracker/closing-soon").then((r) => {
      setSoon(r.data);
      if (r.data.length > 0 && !sessionStorage.getItem("closingSoonDismissed")) setPopup(true);
    }).catch(() => {});
  }, []);

  const closePopup = (o) => {
    setPopup(o);
    if (!o) sessionStorage.setItem("closingSoonDismissed", "1");
  };

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-7xl">
      <Dialog open={popup} onOpenChange={closePopup}>
        <DialogContent className="max-w-md" data-testid="closing-soon-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> {soon.length} bid(s) closing within 2 days
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {soon.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm">
                <div>
                  <div className="font-semibold text-slate-900">{b.bid_number}</div>
                  <div className="text-xs text-slate-600">Last date: {b.last_date} · {b.eligibility}</div>
                </div>
                <span className="text-xs font-bold text-red-600">{b.days_left}d left</span>
              </div>
            ))}
          </div>
          <Button onClick={() => { closePopup(false); navigate("/tracker"); }} className="w-full rounded-lg bg-slate-900 hover:bg-slate-800 mt-2" data-testid="popup-view-tracker">
            Open Bid Tracker <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </DialogContent>
      </Dialog>

      {soon.length > 0 && (
        <div className="mb-6 rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="overview-closing-banner">
          <div className="flex items-start gap-3">
            <CalendarClock className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm text-red-800">
              <strong>{soon.length} bid(s) closing within 2 days:</strong>{" "}
              {soon.map((b) => `${b.bid_number} (${b.last_date})`).join(", ")}
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-lg shrink-0 border-red-300 text-red-700" onClick={() => navigate("/tracker")}>View</Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">Dashboard</div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
            Your GeM bidding paperwork, done in seconds.
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl text-sm sm:text-base leading-relaxed">
            Pick a bid, generate every required declaration on your official letterhead, and let AI draft your
            point-by-point ATC compliance. Download as PDF or editable Word.
          </p>
        </div>
        <Button
          data-testid="overview-new-bid-btn"
          onClick={() => navigate("/bids")}
          className="rounded-full bg-slate-900 hover:bg-slate-800 h-11 px-6 shrink-0"
        >
          <Plus className="h-4 w-4 mr-2" /> New Bid
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {STAT.map((s) => (
          <Card key={s.key} data-testid={`stat-${s.key}`} className="p-5 border-border rounded-xl">
            <div className={`h-10 w-10 rounded-lg ${s.bg} flex items-center justify-center`}>
              <s.icon className={`h-5 w-5 ${s.color}`} />
            </div>
            <div className="font-head text-3xl font-extrabold mt-4">{stats[s.key] ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Card className="lg:col-span-2 p-6 rounded-xl border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-head text-xl font-bold tracking-tight">Recent Bids</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/bids")} className="text-emerald-700">
              View all <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          {bids.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">
              No bids yet. Create your first bid to start generating documents.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {bids.slice(0, 6).map((b) => (
                <button
                  key={b.id}
                  data-testid={`overview-bid-${b.id}`}
                  onClick={() => navigate(`/bids/${b.id}`)}
                  className="w-full flex items-center justify-between py-3 text-left hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{b.product_name || "Untitled item"}</div>
                    <div className="font-mono text-xs text-slate-500 truncate">{b.bid_number}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-medium">
                      {b.doc_count} docs
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 rounded-xl border-border bg-slate-900 text-white">
          <ShieldCheck className="h-8 w-8 text-emerald-400" />
          <h3 className="font-head text-lg font-bold mt-4">Set up once, reuse forever</h3>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">
            Your company letterhead, GSTIN, Udyam & DPIIT details are saved and auto-filled into every document.
          </p>
          <div className="space-y-2 mt-5">
            <Button data-testid="quick-company" onClick={() => navigate("/company")} variant="secondary" className="w-full justify-between rounded-lg bg-white/10 hover:bg-white/20 text-white border-0">
              Company Letterhead <Building />
            </Button>
            <Button data-testid="quick-buyers" onClick={() => navigate("/buyers")} variant="secondary" className="w-full justify-between rounded-lg bg-white/10 hover:bg-white/20 text-white border-0">
              Buyers Library <Users className="h-4 w-4" />
            </Button>
            <Button data-testid="quick-products" onClick={() => navigate("/products")} variant="secondary" className="w-full justify-between rounded-lg bg-white/10 hover:bg-white/20 text-white border-0">
              Products Library <Package className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Building() {
  return <ArrowRight className="h-4 w-4" />;
}
